import React, { useEffect, useRef } from 'react';
import {
  Button,
  FormControl,
  FormControlLabel, FormHelperText,
  Grid, InputLabel, MenuItem,
  Switch,
  TextField,
  Typography,
  ButtonGroup, Tooltip, IconButton,
} from '@mui/material';
import {useDispatch, useSelector} from 'react-redux';
import {Controller, useFormContext} from 'react-hook-form';
import PropTypes from 'prop-types';

import CustomPanel from '../../../../common/CustomPanel';
import { renderHiddenRegister } from '../../../../common/FormHiddenField';
import OutlinedSelect from '../../../../common/OutlinedSelect';
import { selectRelationshipBillingData, selectDivisionPolitica } from '../utils/selectors';
import SearchIcon from '@mui/icons-material/Search';
import { isValidId } from '../../../../../utils/id-validator';
import { requestClientInformation } from '../actions/client_information';
import { addSweetAlert, closeSweetAlert } from '../../../../common/SweetAlert/actions/sweet_alert';
import * as Constants from '../../../../../utils/Constants';

import useAxios from 'axios-hooks';

const BillData = (props) => {
  const { readOnly } = props;
  const { register, control, formState: { errors }, watch, setValue, clearErrors, getValues } = useFormContext();
  const relationship = useSelector(selectRelationshipBillingData);
  const dispatch = useDispatch();
  const { requestData: quoteData } = useSelector((state) => state.quote);

  const quote = useSelector((state) => state.quote);
  const getApplication = useSelector((state) => state.getApplication);
  const watchContact = watch('contact');
  const anotherPersonWatch = watch('billingData.anotherPerson');
  const identificationWatch = watch('billingData.identification');
  const { name: nameHolder = '', lastName: lastNameHolder = '', identification: identificationHolder = '',nacionality: nacionalityHolder = '' } = watch('insuranceHolder') || {};
  const { name: nameMain = '', lastName: lastNameMain = '', identification: identificationMain = '',nacionality: nacionalityMain = '' } = watch('insuranceMainInsured') || {};
  const [isIdentification, setIsIdentification] = React.useState(null);
  const identification = watch('billingData.identification');
  const [foundIdentification, setFoundIdentificacion] = React.useState(null);
  const countries = useSelector(selectDivisionPolitica);
  const nacionalityWatch = watch('billingData.nacionality');

  const [
    { loading, error, response }, getIdentificationInfo,
  ] = useAxios(
    {
      url: `${process.env.REACT_APP_CLIENT_INFORMATION_VALIDATION}/${identification}/${quoteData?.productId}/false`,
      method: 'GET',
    },
    { manual: true },
  );

  const resetBillingToContact = () => {

    const person = { name: '', lastName: '', identification: '',nacionality: '' };
    if (quote?.requestData?.withDifferentHolder) {
      person.name = nameHolder;
      person.lastName = lastNameHolder;
      person.identification = identificationHolder;
      person.nacionality = nacionalityHolder;
    } else {
      person.name = nameMain;
      person.lastName = lastNameMain;
      person.identification = identificationMain;
      person.nacionality = nacionalityMain;
    }


    setValue('billingData.name', person.name);
    setValue('billingData.lastName', person.lastName);
    setValue('billingData.identification', person.identification);
    setValue('billingData.address', watchContact?.address || '');
    setValue('billingData.phone', watchContact?.cellularPhone || '');
    setValue('billingData.email', watchContact?.email || '');
    setValue('billingData.nacionality', person.nacionality);
    clearErrors([
      'billingData.name',
      'billingData.lastName',
      'billingData.identification',
      'billingData.address',
      'billingData.phone',
      'billingData.email',
      'billingData.nacionality',
    ]);
  };

  useEffect(() => {
    if (!anotherPersonWatch) {
      resetBillingToContact();
      setFoundIdentificacion(null);
    } else {
      resetCompleteForm();
    }
  }, [anotherPersonWatch]);

  const setInfo = () => {
    const person = { name: '', lastName: '', identification: '' };
    const samePerson = !getValues('billingData.anotherPerson');
    if (samePerson) {
      if (quote?.requestData?.withDifferentHolder) {
        person.name = nameHolder;
        person.lastName = lastNameHolder;
        person.identification = identificationHolder;
        person.nacionality = nacionalityHolder;
      } else {
        person.name = nameMain;
        person.lastName = lastNameMain;
        person.identification = identificationMain;
        person.nacionality = nacionalityMain;
      }
      setValue('billingData.name', person.name);
      setValue('billingData.lastName', person.lastName);
      setValue('billingData.identification', isIdentification ? person.identification : `${person.identification}001`);
      setValue('billingData.nacionality', person.nacionality);
    } else {
      if(isIdentification) {

      }
      if (quote?.requestData?.personalData?.billingData) {
        setDataFromQuote();

      }
    }
  };

  const setDataFromQuote = () => {
    const billingDataToSet = quote?.requestData?.personalData?.billingData;
    setValue('billingData.name', billingDataToSet.name);
    setValue('billingData.lastName', billingDataToSet.lastName);
    setValue('billingData.identification', billingDataToSet.identification);
    setValue('billingData.address', billingDataToSet.address);
    setValue('billingData.phone', billingDataToSet.phone);
    setValue('billingData.email', billingDataToSet.email);
    setValue('billingData.relationshipBeneficiary', billingDataToSet.relationshipBeneficiary);
    setValue('billingData.nacionality', billingDataToSet.nacionality);
  }

  const setAddress = () => {
    if (!getValues('billingData.anotherPerson')) {
      if (watchContact.address || watchContact.cellularPhone || watchContact.email ) {
        clearErrors([
          'billingData.address',
          'billingData.phone',
          'billingData.email',
        ]);
        setValue('billingData.address', watchContact?.address || '');
        setValue('billingData.phone', watchContact?.cellularPhone || '');
        setValue('billingData.email', watchContact?.email || '');
      }
    }
  };

  useEffect(() => {
    if (getApplication?.response?.InsuranceApplicationBilling) {
      const insuranceApplicationBilling = getApplication?.response?.InsuranceApplicationBilling;
      setValue('billingData.anotherPerson', insuranceApplicationBilling?.AnotherPerson);
      setValue('billingData.name', insuranceApplicationBilling?.Name?.trim() || '');
      setValue('billingData.lastName', insuranceApplicationBilling?.LastName?.trim() || '');
      setValue('billingData.address', insuranceApplicationBilling?.Address?.trim() || '');
      setValue('billingData.phone', insuranceApplicationBilling?.Phone?.trim() || '');
      setValue('billingData.email', insuranceApplicationBilling?.Email?.trim() || '');
      setValue('billingData.identification', insuranceApplicationBilling?.Identification?.trim() || '');
      setValue('billingData.relationshipBeneficiary', insuranceApplicationBilling?.RelationshipBeneficiary?.trim() || '');
      setValue('billingData.isIdentification', insuranceApplicationBilling?.Identification?.trim().length === 10 ? true : false);
      setValue('billingData.nacionality', insuranceApplicationBilling?.nacionality?.trim() || '');

      const identificationLength = insuranceApplicationBilling?.Identification?.trim().length;
      if (identificationLength === 10) {
        setIsIdentification(true);
      } else {
        setIsIdentification(false);
      }
    }
  }, [getApplication.response]);

  const relacionRef = useRef();
  const nombresRef = useRef();
  const apellidosRef = useRef();
  const rucRef = useRef();
  const direccionRef = useRef();
  const telefonoRef = useRef();
  const mailRef = useRef();
  const nacionalityRef= useRef();

  useEffect(() => {
    if (isIdentification !== null) {
      setInfo();
      setAddress();
    }
  }, [isIdentification]);

  const handleSearchIdentification = () => {
    if (identification && isIdentification) {
      if (isValidId(identification)) {
        getIdentificationInfo();
      } else {
        dispatch(addSweetAlert({
          type: 'warning',
          title: Constants.MESSAGE_INVALID_IDENTIFICATION,
          showCancel: false,
          onConfirmLabel: 'Aceptar',
          onConfirm: () => {
            dispatch(closeSweetAlert());
          },
        }));
      }
    }
  };

  useEffect(() => {
    if (response && response?.status === 200) {

      if (response && response?.status === 200) {
        if (response?.data.validationResult.result !== 'SUCCESS' ) {
          const result = response?.data.validationResult.result;
          if (result === 'INACTIVATE_PERSON' || result === 'DISABLED_PERSON' ) {
            const type = result === 'CONTINUE_WITH_CLIENT_WARNING' ? 'info' : 'warning';
            const title = result === 'CONTINUE_WITH_CLIENT_WARNING' ? 'Documento no válido' : '';
            dispatch(addSweetAlert({
              type,
              title,
              content: (
                  <Grid
                      container
                      direction="row"
                      justifyContent="center"
                      alignItems="center"
                      spacing={3}
                      style={{ marginTop: 10 }}
                  >
                    <Typography
                        variant="h6"
                    >
                      {`${response?.data.validationResult.messages[0]}`}
                    </Typography>
                  </Grid>
              ),
              showCancel: false,
              onConfirmLabel: 'Aceptar',
              onConfirm: () => {
                resetPartialForm();
                dispatch(closeSweetAlert());
              },
            }));
            return;
          }
        }
      }

      if(response?.data?.personDto) {
        setFoundIdentificacion(true);
        clearErrors([
          'billingData.name',
          'billingData.lastName',
          'billingData.nacionality',
        ]);
        setValue('billingData.name', response?.data?.personDto.name || '');
        setValue('billingData.lastName', response?.data?.personDto.lastName || '');
        setValue('billingData.nacionality', response?.data?.personDto.nacionality || '');
      } else {
        dispatch(addSweetAlert({
          type: 'warning',
          title: 'No existe un registro con la identificaci\u00F3n ingresada',
          showCancel: false,
          onConfirmLabel: 'Aceptar',
          onConfirm: () => {
            setFoundIdentificacion(false);
            resetPartialForm();
            dispatch(closeSweetAlert());
          },
        }));
      }
    } else if (error) {
      dispatch(addSweetAlert({
        type: 'info',
        title: 'Documento no encontrado',
        content: Constants.MESSAGE_REGISTRO_CIVIL,
        showCancel: false,
        onConfirmLabel: 'Continuar',
        onConfirm: () => {
          setFoundIdentificacion(false);
          resetPartialForm();
          dispatch(closeSweetAlert());
        },
      }));
    }
  }, [response]);

  const resetCompleteForm = () => {
    clearErrors([
      'billingData.name',
      'billingData.lastName',
      'billingData.address',
      'billingData.phone',
      'billingData.email',
      'billingData.identification',
      'billingData.relationshipBeneficiary',
      'billingData.nacionality'
    ]);
    setValue('billingData.name', '');
    setValue('billingData.lastName', '');
    setValue('billingData.address', '');
    setValue('billingData.phone', '');
    setValue('billingData.email', '');
    setValue('billingData.identification', '');
    setValue('billingData.relationshipBeneficiary', '');
    setValue('billingData.nacionality', '');
  };

  const resetPartialForm = () => {
    clearErrors([
      'billingData.name',
      'billingData.lastName',
      'billingData.nacionality',
    ]);
    setValue('billingData.name', '');
    setValue('billingData.lastName', '');
    setValue('billingData.nacionality', '');
  };

  const onClickIsIdentification = (value) => {
    setIsIdentification(value);
    setValue('billingData.isIdentification', value);
  };

  return (
    <CustomPanel title="Datos de Facturación" border={false}>
      <Grid
        container
        direction="row"
        spacing={3}
      >
        <Grid item md={anotherPersonWatch ? 8 : 12} xs={12}>
          <FormControlLabel
            control={(
              <Controller
                name="billingData.anotherPerson"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <Switch
                    {...field}
                    color="primary"
                  />
                )}
              />
            )}
            label={(
              <Typography variant="h5">
                Factura a nombre de otra persona natural
              </Typography>
            )}
            labelPlacement="start"
          />
        </Grid>
        <Controller
          name="billingData.isIdentification"
          control={control}
          defaultValue={false}
          render={renderHiddenRegister}
        />
        {anotherPersonWatch && (
          <Grid item md={4} xs={12}>
            <FormControl
              fullWidth
              error={errors.billingData?.hasOwnProperty('relationshipBeneficiary')}
            >
              <InputLabel id="id-relationship">RELACIÓN CON TITULAR</InputLabel>
              <Controller
                name="billingData.relationshipBeneficiary"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <OutlinedSelect {...field} labelId="id-relationship" label="RELACIÓN CON TITULAR" inputRef={relacionRef} onFocus={() => relacionRef.current.focus()} inputProps={{ readOnly }}>
                    <MenuItem value="">
                      <em>Seleccione..</em>
                    </MenuItem>
                    {
                      relationship?.map((relation) => (
                        <MenuItem
                          key={relation.id}
                          value={relation.id}
                        >
                          {relation.description}
                        </MenuItem>
                      ))
                    }
                  </OutlinedSelect>
                )}
              />
              <FormHelperText>
                {errors.billingData?.relationshipBeneficiary?.message}
              </FormHelperText>
            </FormControl>
          </Grid>
        )}
        <Grid item md={12} xs={12}>
          <ButtonGroup color="primary">
            <Button variant={isIdentification ? 'contained' : 'outlined'} onClick={() => onClickIsIdentification(true)}>CÉDULA</Button>
            <Button variant={isIdentification === null ? 'outlined' : !isIdentification ? 'contained' : 'outlined'} onClick={() => onClickIsIdentification(false)}>RUC</Button>
          </ButtonGroup>
        </Grid>
        <Grid item md={4} xs={12}>
          <Controller
            name="billingData.name"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                inputRef={nombresRef}
                onFocus={() => nombresRef.current.focus()}
                autoFocus
                margin="dense"
                label="NOMBRE"
                autoComplete="off"
                type="text"
                fullWidth
                disabled={(!anotherPersonWatch && foundIdentification === null)
                || (anotherPersonWatch && foundIdentification === null && isIdentification === null )
                || (anotherPersonWatch && foundIdentification === null && isIdentification )
                || (anotherPersonWatch && foundIdentification && isIdentification )}
                error={errors.billingData?.name && true}
                helperText={errors.billingData?.name && errors.billingData?.name.message}
                InputProps={{ readOnly }}
              />
            )}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <Controller
            name="billingData.lastName"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                inputRef={apellidosRef}
                onFocus={() => apellidosRef.current.focus()}
                autoFocus
                margin="dense"
                label="APELLIDOS"
                autoComplete="off"
                type="text"
                fullWidth
                disabled={(!anotherPersonWatch && foundIdentification === null)
                || (anotherPersonWatch && foundIdentification === null && isIdentification === null )
                || (anotherPersonWatch && foundIdentification === null && isIdentification )
                || (anotherPersonWatch && foundIdentification && isIdentification )}
                error={errors.billingData?.lastName && true}
                helperText={errors.billingData?.lastName && errors.billingData?.lastName.message}
                InputProps={{ readOnly }}
              />
            )}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <Controller
            name="billingData.identification"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                inputRef={rucRef}
                onFocus={() => rucRef.current.focus()}
                margin="dense"
                label={isIdentification || isIdentification === null ? 'CI' : 'RUC'}
                autoComplete="off"
                type="number"
                fullWidth
                disabled={!anotherPersonWatch}
                error={errors.billingData?.identification && true}
                helperText={errors.billingData?.identification && errors.billingData?.identification.message}
                InputProps={{
                  endAdornment: (
                    anotherPersonWatch && isIdentification && <Tooltip title="Buscar" arrow>
                      <IconButton
                        aria-label="search"
                        size="small"
                        onClick={handleSearchIdentification}
                      >
                        <SearchIcon />
                      </IconButton>
                    </Tooltip>
                  ),
                  readOnly: !anotherPersonWatch,
                }}
              />
            )}
          />
        </Grid>
        <Grid item md={4} xs={12}>
        <FormControl
              fullWidth
              error={errors.billingData?.hasOwnProperty('nacionality')}
            >
              <InputLabel id="id-nacionality">NACIONALIDAD</InputLabel>
              <Controller
                name="billingData.nacionality"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <OutlinedSelect
                    {...field}
                    labelId="id-nacionality"
                    label="NACIONALIDAD"
                    inputRef={nacionalityRef}
                    onFocus={() => nacionalityRef.current.focus()}
                    inputProps={{ readOnly }}
                    disabled={(!anotherPersonWatch && foundIdentification === null)
                      || (anotherPersonWatch && foundIdentification === null && isIdentification === null )
                      || (anotherPersonWatch && foundIdentification === null && isIdentification )
                      || (anotherPersonWatch && foundIdentification && isIdentification )}
                  >
                    <MenuItem value="">
                      <em>Seleccione..</em>
                    </MenuItem>
                    {
                      countries?.map((relation) => (
                        <MenuItem
                          key={relation.id}
                          value={relation.description}
                        >
                          {relation.description}
                        </MenuItem>
                      ))
                    }
                  </OutlinedSelect>
                )}
              />
              <FormHelperText>
                {errors.billingData?.nacionality?.message}
              </FormHelperText>
            </FormControl>
        </Grid>
        <Grid item md={12} xs={12}>
          <Controller
            name="billingData.address"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                inputRef={direccionRef}
                onFocus={() => direccionRef.current.focus()}
                margin="dense"
                label="DIRECCIÓN"
                autoComplete="off"
                type="text"
                fullWidth
                disabled={!anotherPersonWatch}
                error={errors.billingData?.address && true}
                helperText={errors.billingData?.address && errors.billingData?.address.message}
                InputProps={{ readOnly }}
              />
            )}
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <Controller
            name="billingData.phone"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                inputRef={telefonoRef}
                margin="dense"
                label="CELULAR"
                onFocus={() => telefonoRef.current.focus()}
                autoComplete="off"
                type="string"
                inputProps={{ maxLength: 10 }}
                fullWidth
                disabled={!anotherPersonWatch}
                error={errors.billingData?.phone && true}
                helperText={errors.billingData?.phone && errors.billingData?.phone.message}
                InputProps={{ readOnly }}
              />
            )}
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <Controller
            name="billingData.email"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                inputRef={mailRef}
                onFocus={() => mailRef.current.focus()}
                margin="dense"
                label="EMAIL"
                autoComplete="off"
                type="text"
                fullWidth
                disabled={!anotherPersonWatch}
                error={errors.billingData?.email && true}
                helperText={errors.billingData?.email && errors.billingData?.email.message}
                InputProps={{ readOnly }}
              />
            )}
          />
        </Grid>
      </Grid>
    </CustomPanel>
  );
};

BillData.propTypes = {
  readOnly: PropTypes.bool.isRequired,
};

export default BillData;
